// src/pages/legal/LegalDocumentPage.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MainNavigation from '../../components/common/MainNavigation';
import SiteFooter from '../../components/common/SiteFooter';
import { legalService, LegalDocument, LegalSlug } from '../../services/legalService';
import StaticPageSeo from '../../seo/StaticPageSeo';
import '../../styles/LegalPage.css';

interface LegalDocumentPageProps {
  slug: LegalSlug;
  /** Fallback heading shown while loading or if the document can't be found. */
  fallbackTitle: string;
}

/**
 * Legal documents are served from one component but live at six distinct public
 * URLs. Without this map they would all share the CRA template's single title
 * and have no canonical, so search engines would see six duplicates.
 */
const LEGAL_SLUG_TO_PATH: Record<LegalSlug, string> = {
  'terms-of-service': '/terms',
  'privacy-policy': '/privacy',
  'provider-agreement': '/provider-agreement',
  'cancellation-policy': '/cancellation-policy',
  'community-standards': '/community-standards',
  'affiliate-terms': '/affiliate-terms',
};

/**
 * Generic renderer for all versioned legal documents. Content always comes
 * from GET /api/legal/:slug — nothing is hardcoded here, so future policy
 * revisions only require updating the backend content, not shipping a new
 * web build.
 */
const LegalDocumentPage: React.FC<LegalDocumentPageProps> = ({ slug, fallbackTitle }) => {
  const [document, setDocument] = useState<LegalDocument | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchDocument = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const doc = await legalService.getDocument(slug);
        if (isMounted) {
          setDocument(doc);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(
            err?.status === 404
              ? 'This document is not available yet. Please check back soon.'
              : 'We couldn\u2019t load this document right now. Please try again later.'
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchDocument();
    return () => {
      isMounted = false;
    };
  }, [slug]);

  return (
    <div className="legal-page">
      <StaticPageSeo path={LEGAL_SLUG_TO_PATH[slug]} />
      <MainNavigation variant="solid" />

      <main className="legal-page__main" id="main-content">
        <div className="legal-page__container">
          {isLoading && (
            <header className="legal-page__header">
              <h1 className="legal-page__title">{fallbackTitle}</h1>
              <p className="legal-page__subtitle">Loading\u2026</p>
            </header>
          )}

          {!isLoading && error && (
            <div className="legal-page__error" role="alert">
              <h1 className="legal-page__title">{fallbackTitle}</h1>
              <p>{error}</p>
              <p>
                <Link to="/">Return to home</Link>
              </p>
            </div>
          )}

          {!isLoading && !error && document && (
            <div className="legal-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{document.bodyMarkdown}</ReactMarkdown>
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default LegalDocumentPage;
